import { useCallback, useEffect, useRef } from 'react';
import { UseLoadingTimeoutsOptions } from '@interfaces';
import { TimeoutMap } from '@types';


/**
 * Manages keyed loading timeouts (one timer per key) and cleans them up on unmount.
 * Use when tracking async operations that should fail after a per-key timeout.
 */
export function useLoadingTimeouts<Key extends string>(
  keys: readonly Key[],
  { timeoutMs = 30000 }: UseLoadingTimeoutsOptions = {},
) {
  // Lazily track timers without tying lifecycle to the `keys` array reference.
  const timeoutsRef = useRef<TimeoutMap<Key>>({} as TimeoutMap<Key>);

  // Ensure new keys are known but never clear existing timers on re-renders.
  useEffect(() => {
    keys.forEach((key) => {
      if (!(key in timeoutsRef.current)) {
        timeoutsRef.current[key] = null;
      }
    });
  }, [keys]);

  const clearTimeoutForKey = useCallback((key: Key) => {
    const timeoutId = timeoutsRef.current[key];
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current[key] = null;
    }
  }, []);

  const startTimeout = useCallback(
    (key: Key, onTimeout: () => void, overrideMs?: number) => {
      clearTimeoutForKey(key);
      const resolvedMs = overrideMs ?? timeoutMs;
      timeoutsRef.current[key] = setTimeout(() => {
        timeoutsRef.current[key] = null;
        onTimeout();
      }, resolvedMs);
    },
    [clearTimeoutForKey, timeoutMs],
  );

  useEffect(() => {
    return () => {
      const timeouts = Object.values(timeoutsRef.current) as Array<ReturnType<typeof setTimeout> | null>;
      timeouts.forEach((timeoutId) => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      });
    };
  }, [clearTimeoutForKey]);

  return {
    startTimeout,
    clearTimeout: clearTimeoutForKey,
  };
}
